/**
 * Трек найденного судна: словарь операций и правила его показа.
 *
 * Заказ владельца 2026-08-12: нашёл судно поиском, кликнул — можно показать и
 * скрыть его путь. Путь берётся тот же, что в разделе «Флот»: позиции АИС,
 * покрашенные операциями (переход/поиск/промысел/дрейф/стоянка) из предрасчёта
 * `ais_track_segment` — см. docs/plans/PLAN_2026-08-03-vessel-operations-segmentation.md.
 *
 * Правила продублированы из `fleet-app/src/trackMap.ts` намеренно: витрины
 * собираются раздельно и общего пакета не имеют. Цвета обязаны совпадать —
 * человек ходит из «Флота» в кабинет и обратно, и «промысел» должен быть одного
 * цвета в обоих местах. Меняешь здесь — меняй там.
 *
 * ПОДПИСИ, в отличие от цветов, тут не лежат: кабинет двуязычный, и слова живут
 * в его каталоге (`models.vesselOps.*`). У `fleet-app` витрина одноязычная — там
 * подписи остаются на месте.
 */
import { VESSEL_NEUTRAL_COLOR, vesselOpsRamp } from '../brand/mapColors'
import { t } from '../i18n'

/** Операции судна — те же имена, что шлёт бэкенд. */
export type VesselOperation =
  | 'steaming' | 'searching' | 'fishing' | 'drifting' | 'moored' | 'no_data'

/** Порядок операций — от «быстрых» к «стоящим»; им же идёт легенда и им же
 *  раскладывается палитра домена (`vessel-ops` в бренд-паке). */
const OPERATION_ORDER: VesselOperation[] = [
  'steaming', 'searching', 'fishing', 'drifting', 'moored', 'no_data',
]

const LABEL_KEYS = new Map<VesselOperation, string>([
  ['steaming', 'models.vesselOps.steaming'],
  ['searching', 'models.vesselOps.searching'],
  ['fishing', 'models.vesselOps.fishing'],   // акцент: ради него всё
  ['drifting', 'models.vesselOps.drifting'],
  ['moored', 'models.vesselOps.moored'],
  ['no_data', 'models.vesselOps.noData'],    // рисуется пунктиром
])

/** Легенда операций: цвет = то, чем красятся линии на карте (единый источник).
 *
 *  Функция, а не константа: гамма домена приезжает позже загрузки модуля. */
export function operationLegend(): {
  operation: VesselOperation
  labelKey: string
  color: string
}[] {
  const ramp = vesselOpsRamp()
  return OPERATION_ORDER.map((operation, i) => ({
    operation,
    labelKey: LABEL_KEYS.get(operation) as string,
    // Палитра короче набора операций (битый ответ) — недостающие берут
    // нейтральный: легенда без цвета хуже легенды с серым.
    color: ramp[i] ?? VESSEL_NEUTRAL_COLOR,
  }))
}

/** Цвет операции; незнакомая (новая версия правил на старом фронте) → нейтральный. */
export function operationColor(operation: VesselOperation | null | undefined): string {
  if (!operation) return VESSEL_NEUTRAL_COLOR
  const i = OPERATION_ORDER.indexOf(operation)
  if (i < 0) return VESSEL_NEUTRAL_COLOR
  return vesselOpsRamp()[i] ?? VESSEL_NEUTRAL_COLOR
}

/** Подпись операции на языке кабинета. */
export function operationLabel(operation: VesselOperation | null | undefined): string {
  const key = operation ? LABEL_KEYS.get(operation) : undefined
  return t(key ?? 'models.vesselOps.unlabelled')
}

/** MapLibre `match`: properties.operation → цвет линии сегмента. */
export function operationColorExpr(): unknown[] {
  const expr: unknown[] = ['match', ['get', 'operation']]
  for (const e of operationLegend()) expr.push(e.operation, e.color)
  expr.push(VESSEL_NEUTRAL_COLOR)
  return expr
}

/** Пунктир разрыва связи: молчание обязано читаться как разрыв, а не как ход
 *  по прямой, которого не было. */
export const GAP_DASHARRAY = [2, 2]

/** `line-dasharray` в MapLibre — не выражение, а свойство слоя, поэтому разрывы
 *  и обычные операции рисуются ДВУМЯ слоями с зеркальными фильтрами. */
export function operationFilter(kind: 'gap' | 'solid'): unknown[] {
  const isGap = ['==', ['get', 'operation'], 'no_data']
  return [
    'all',
    ['==', ['geometry-type'], 'LineString'],
    kind === 'gap' ? isGap : ['!', isGap],
  ]
}

/** Полупрозрачность сомнительных сегментов: показываем, но не выдаём за измерение. */
export function operationOpacityExpr(): unknown[] {
  return ['case', ['==', ['get', 'low_confidence'], true], 0.45, 0.9]
}

// --- Период -----------------------------------------------------------------

const DAY_MS = 86_400_000

/** Сколько суток трека показываем по умолчанию. Месяц — это и рейс, и сезонный
 *  кусок промысла; больше превращает карту в клубок. */
export const TRACK_DEFAULT_DAYS = 30

function ms(date: string): number {
  const [y, m, d] = date.split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

function iso(msUtc: number): string {
  return new Date(msUtc).toISOString().slice(0, 10)
}

/** Окно по умолчанию: месяц, кончающийся ПРОСМАТРИВАЕМЫМ днём.
 *
 *  Именно им, а не сегодняшним: человек мотает карту в прошлое, и трек «за
 *  последний месяц от сегодня» показал бы не ту воду, на которую он смотрит. */
export function defaultTrackRange(
  date: string, days: number = TRACK_DEFAULT_DAYS,
): { from: string; to: string } {
  return { from: iso(ms(date) - (days - 1) * DAY_MS), to: date }
}

export interface TrackMeta {
  reason?: string | null
  count?: number
  /** Часы по операциям за период — из них строится легенда трека. */
  hours?: Record<string, number>
  available_from?: string | null
  available_to?: string | null
}

/**
 * Каким периодом заменить текущий, если в нём данных нет, а у судна они есть.
 *
 * Разметка АИС лежит в своём периоде (у нас — с марта 2026). Без подмены человек
 * видит «трека нет» там, где данные есть, и должен догадаться поправить даты
 * руками. `null` — менять нечего или уже подставлено (защита от петли запросов).
 */
export function periodFromMeta(
  meta: TrackMeta, current: { from: string; to: string },
): { from: string; to: string } | null {
  if (meta.reason !== 'no_data') return null
  const from = meta.available_from
  const to = meta.available_to
  if (!from || !to) return null
  if (current.from === from && current.to === to) return null
  return { from, to }
}

export interface OperationHoursRow {
  operation: VesselOperation
  label: string
  color: string
  hours: number
}

/** `meta.hours` → строки легенды трека, от большего к меньшему.
 *
 *  Легенда строится по ТОМУ, ЧТО ЕСТЬ в ответе, а не по полному словарю: шесть
 *  строк, из которых заняты две, читаются как «остального не было», хотя на
 *  самом деле его просто нет в этом периоде. */
export function operationHours(
  hours: Partial<Record<VesselOperation, number>> | undefined,
): OperationHoursRow[] {
  if (!hours) return []
  return (Object.entries(hours) as [VesselOperation, number][])
    .filter(([, value]) => value > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([operation, value]) => ({
      operation,
      label: operationLabel(operation),
      color: operationColor(operation),
      hours: value,
    }))
}

/** «03.2026» — месяц и год из ISO-даты, для строки о доступном периоде. */
function monthYear(date: string): string {
  const [y, m] = date.split('-')
  return `${m}.${y}`
}

/** Строка под кнопкой: почему трека не видно или сколько его.
 *
 *  Молчащая кнопка неотличима от сломанной, а «нет данных» без причины
 *  неотличимо от «судно не ловило». */
export function trackHint(meta: TrackMeta): string {
  if (meta.reason === 'error') return t('models.vesselTrack.error')
  if (meta.reason === 'no_mmsi') return t('models.vesselTrack.noMmsi')
  if (meta.reason === 'no_data') {
    if (meta.available_from && meta.available_to) {
      return t('models.vesselTrack.emptyButAvailable', {
        from: monthYear(meta.available_from),
        to: monthYear(meta.available_to),
      })
    }
    return t('models.vesselTrack.emptyPeriod')
  }
  return t('models.vesselTrack.segments', { count: meta.count ?? 0 })
}
